/**
 * CSA ad-type handler — Custom Search Ads.
 *
 * Reads `page_options.csa` and calls `_googCsa("ads", ...)`. Ported from
 * @serp/client's google-csa/ads.ts. The renderer-served SERP pages populate
 * `page_options.csa` (distinct from the publisher `page_options.rsoc` field),
 * so this handler is what fires on SSR surfaces.
 *
 * Differences from the serp original:
 * - No personalization branch. CSA is consent-exempt in the unified client
 *   (see modules/google-csa/index.ts), so it always runs in full/personalized
 *   mode — the `personalizedAds=false` path is intentionally dropped.
 * - Beacon-cookie stamping is delegated to the `stampBeaconCookies` helper
 *   rather than an inline loop.
 *
 * Bootstrap: renderer SSR pages inject the `_googCsa` stub and `ads.js` into
 * the head (@serp/renderer document/index.ts `renderCsaPageOptions`), and the
 * RSoC path inlines them in `google-rsoc.onResponse`. When neither has run
 * (a partner page whose CSA groups target its own containers), this handler
 * loads them itself, but only for units whose container is in this document.
 * Units drawn inside an embedded_serp iframe run CSA in-frame
 * (@serp/renderer fragments/csa-embed.ts) and must not be requested twice.
 */

import { CONSOLE_CAPTURE_PREFIX } from "@ascent/debugger";
import type * as PAPICore from "@ascent/papi-core";
import {
  appendPartnerCookies,
  sendPingback as sendBeacon,
} from "@ascent/papi-core/client";
import {
  TrackingEvent,
  observeAdContainer,
  observeInView,
} from "../../tracking-events";
import { inlineScript, loadScript, onDomReady } from "../../util";
import { stampBeaconCookies } from "./beacons";
import { setupGoogleIframeClickTracking } from "./click-tracking";
import { CSA_SCRIPT_SRC, CSA_STUB } from "./script";
import type {
  GoogleBlock,
  GoogleCsaPageOptions,
  GoogleResultType,
  GoogleUnitLevelParams,
} from "./types";

/**
 * CSA rejects requests that mix units with conflicting layout parameters. Any
 * difference in these keys forces us to spin up a separate `_googCsa` call.
 */
const BLOCK_OPTION_KEYS = ["styleId"] as const;

/**
 * Some result types require hard-coded overrides regardless of the payload
 * (e.g. related searches are rendered in a grid).
 */
const DEFAULT_OVERRIDES: Partial<
  Record<GoogleResultType, Record<string, unknown>>
> = {};

/**
 * Google Custom Search Ads bootstrapper exposed globally.
 * @see https://support.google.com/adsense/answer/9055049
 */
declare const _googCsa: (
  product: string,
  options: GoogleCsaPageOptions,
  ...units: GoogleUnitLevelParams[]
) => void;

/**
 * True when the response carries CSA page options. Drives whether the module
 * runs this handler.
 */
export function hasCsa(response: PAPICore.Response): boolean {
  return !!response.page_options?.csa?.csa_page_options;
}

/**
 * Groups Google ad units by the CSA options they require, allowing us to call
 * `_googCsa` once per unique configuration.
 */
export function collectBlocks(
  response: PAPICore.Response,
  resultTypes: GoogleResultType | GoogleResultType[],
): GoogleBlock[] {
  const normalizedResultTypes = Array.isArray(resultTypes)
    ? Array.from(new Set(resultTypes))
    : [resultTypes];

  if (!normalizedResultTypes.length) {
    return [];
  }

  const targetResultTypes = new Set<GoogleResultType>(normalizedResultTypes);
  const blocks = new Map<string, GoogleBlock>();

  for (const region of response.regions ?? []) {
    for (const group of region.groups ?? []) {
      if (group.backend !== "google") {
        continue;
      }

      const resultType = group.result_type;
      if (
        !isGoogleResultType(resultType) ||
        !targetResultTypes.has(resultType)
      ) {
        continue;
      }

      const rawUnit =
        (group.unit_level_params as GoogleUnitLevelParams | null) ?? null;
      if (!rawUnit) {
        continue;
      }

      const unit = { ...rawUnit };
      const overrides = resolveOverrides(
        resultType,
        unit,
        extractGroupOverrides(group),
      );
      const key = createBlockKey(overrides);
      const block = blocks.get(key);

      if (block) {
        if (!block.resultTypes.includes(resultType)) {
          block.resultTypes.push(resultType);
        }
        block.units.push(unit);
      } else {
        blocks.set(key, {
          key,
          resultTypes: [resultType],
          pageOptions: overrides,
          units: [unit],
        });
      }
    }
  }

  return Array.from(blocks.values());
}

/**
 * Hydrates Google CSA for all applicable result types. Always personalized —
 * the unified client treats CSA as consent-exempt.
 */
export function initializeCsa(response: PAPICore.Response): void {
  if (typeof window === "undefined") {
    return;
  }

  const csaConfig = response.page_options?.csa;
  if (!csaConfig?.csa_page_options) {
    return;
  }

  const baseOptions: GoogleCsaPageOptions = {
    ...csaConfig.csa_page_options,
  };
  const callback = csaConfig.callback;
  const resultTypes: GoogleResultType[] = ["ads", "aylf"];
  const blocks = collectBlocks(response, resultTypes);

  // Heuristic cross-origin iframe click tracking. Guarded internally against
  // duplicate listener registration, so it is safe to call once per init.
  setupGoogleIframeClickTracking();

  // Observers wait for their container node on their own. Google's loader
  // polls for containers that are not in the DOM yet, so the ad request
  // leaves during head parse.
  for (const block of blocks) {
    for (const unit of block.units) {
      setupCsaContainerObservers(unit, callback);
    }
  }

  // The SERP renderer injects the stub before ramp.js runs, and Google polls
  // for its containers, so those requests can leave during head parse.
  if (typeof _googCsa === "function" && !window.__rampjsCsaHostPage) {
    for (const block of blocks) {
      requestBlock(block, block.units, baseOptions, callback);
    }
    return;
  }

  // ramp.js loads CSA on this page. Containers must exist before the request,
  // so units drawn elsewhere (an embedded_serp iframe) are left out.
  window.__rampjsCsaHostPage = true;
  onDomReady(() => {
    const placed = blocks
      .map((block) => ({
        block,
        units: block.units.filter(claimHostContainer),
      }))
      .filter(({ units }) => units.length > 0);
    if (!placed.length) return;

    loadCsaScript();
    for (const { block, units } of placed) {
      requestBlock(block, units, baseOptions, callback);
    }
  });
}

function requestBlock(
  block: GoogleBlock,
  units: GoogleUnitLevelParams[],
  baseOptions: GoogleCsaPageOptions,
  callback?: PAPICore.Callback,
): void {
  const pageOptions: GoogleCsaPageOptions = {
    ...baseOptions,
    ...block.pageOptions,
  };
  const decorated = units.map((unit) => decorateUnit(unit, callback));

  if (!decorated.length) {
    return;
  }

  _googCsa("ads", pageOptions, ...decorated);

  if (__DEBUG__) {
    console.groupCollapsed(`${CONSOLE_CAPTURE_PREFIX} Custom Search Ads (CSA)`);
    console.log(
      `${CONSOLE_CAPTURE_PREFIX} Result Type`,
      block.resultTypes.map((type) => type.toUpperCase()).join(", "),
    );
    console.log(`${CONSOLE_CAPTURE_PREFIX} Page Options`, pageOptions);
    console.log(`${CONSOLE_CAPTURE_PREFIX} Blocks`, decorated);
    console.groupEnd();
  }
}

/**
 * True when the unit's container is in this document. Copies the unit's click
 * beacon onto the container, where click-tracking.ts reads it, unless the
 * container already carries one (renderer markup).
 */
function claimHostContainer(unit: GoogleUnitLevelParams): boolean {
  const container =
    typeof unit.container === "string"
      ? document.getElementById(unit.container)
      : null;
  if (!container) return false;
  if (
    typeof unit.clickBeaconUrl === "string" &&
    unit.clickBeaconUrl &&
    !container.getAttribute("data-ad-clicked-beacon-url")
  ) {
    container.setAttribute("data-ad-clicked-beacon-url", unit.clickBeaconUrl);
  }
  return true;
}

/** Inline the `_googCsa` stub and load `ads.js`, each at most once per page. */
function loadCsaScript(): void {
  if (typeof _googCsa === "undefined") {
    inlineScript(CSA_STUB);
  }
  if (document.querySelector(`script[src="${CSA_SCRIPT_SRC}"]`) === null) {
    loadScript(CSA_SCRIPT_SRC).catch(() => {
      if (__DEBUG__) {
        console.error("[RampJs] Failed to load Google CSA script.");
      }
    });
  }
}

/**
 * Wire the shared mutation_observed / resize_observed / in_view observers on a
 * CSA unit's container. Encoded on the unit's adLoadedCallback URL as
 * `<event>=true` plus `container=<id>` — the same wire shape the RSoC path
 * uses — so CSA reports a standardized event set. No-ops for units without a
 * container or a string adLoadedCallback URL.
 *
 * mutation_observed / resize_observed are ad-load ground truth (self-disconnect
 * on fire); in_view is a page-lifetime viewport signal, wired separately with
 * observeInView so it is never torn down before a below-the-fold slot scrolls
 * into view. A per-container dataset guard makes this idempotent — initializeCsa
 * can run more than once (multiple ramp.js instances / re-init), and without it
 * a second call would double-wire the observers and double-fire the pingbacks.
 */
function setupCsaContainerObservers(
  unit: GoogleUnitLevelParams,
  callback?: PAPICore.Callback,
): void {
  const containerName = unit.container;
  const adLoadedCallbackUrl = unit.adLoadedCallback;
  if (
    typeof containerName !== "string" ||
    !containerName ||
    typeof adLoadedCallbackUrl !== "string"
  ) {
    return;
  }

  onDomReady(() => {
    const container = document.getElementById(containerName);
    if (!container) return;

    // Idempotency guard — skip if this container is already observed.
    if (container.dataset.rampjsCsaObserved === "1") return;
    container.dataset.rampjsCsaObserved = "1";

    const fireEvent = (event: TrackingEvent) => {
      try {
        const url = new URL(adLoadedCallbackUrl);
        url.searchParams.set(event, "true");
        url.searchParams.set("container", containerName);
        if (callback?.loaded_param) {
          url.searchParams.set("loaded_param", callback.loaded_param);
        }
        appendPartnerCookies(url);
        sendPingback(url);
        if (__DEBUG__) {
          console.log(
            `${CONSOLE_CAPTURE_PREFIX} CSA ${event} for container: '${containerName}'`,
          );
        }
      } catch (error) {
        if (__DEBUG__) {
          console.debug(`Failed to send CSA ${event} pingback:`, error);
        }
      }
    };

    // Ad-load observers (mutation/resize) + the page-lifetime viewport signal.
    observeAdContainer(container, fireEvent);
    observeInView(container, () => fireEvent(TrackingEvent.InView));
  });
}

/**
 * Normalises a unit before we hand it to `_googCsa`.
 */
function decorateUnit(
  unit: GoogleUnitLevelParams,
  callback?: PAPICore.Callback,
): GoogleUnitLevelParams {
  // Stamp partner cookies on the known beacon URLs (shared with the RSoC path).
  const decoratedUnit: GoogleUnitLevelParams = stampBeaconCookies(unit);

  // Handle exception if maxTop and number are both included in the block config
  if (decoratedUnit.maxTop && decoratedUnit.number) {
    delete decoratedUnit.number;
  }

  if (typeof unit.adLoadedCallback === "string") {
    const adLoadedCallbackUrl = unit.adLoadedCallback;

    decoratedUnit.adLoadedCallback = (
      containerName: string,
      adsLoaded: boolean,
      isExperimentVariant: boolean,
      callbackOptions: { termPositions: Record<string, number> },
    ) => {
      try {
        const input = new URL(adLoadedCallbackUrl);

        if (callback?.loaded_param) {
          input.searchParams.set(callback.loaded_param, String(adsLoaded));
          input.searchParams.set("loaded_param", callback.loaded_param);
        }

        if (callback?.options_param) {
          input.searchParams.set("options_param", callback.options_param);
          input.searchParams.set(
            callback.options_param,
            JSON.stringify(callbackOptions),
          );
        }

        appendPartnerCookies(input);
        sendPingback(input);
      } catch (error) {
        console.debug("Failed to send adLoaded pingback:", error);
      }

      window.__showAlgoResults?.();
    };
  } else {
    const existingCallback =
      typeof unit.adLoadedCallback === "function"
        ? unit.adLoadedCallback
        : null;
    decoratedUnit.adLoadedCallback = (
      containerName: string,
      adsLoaded: boolean,
      isExperimentVariant: boolean,
      callbackOptions: { termPositions: Record<string, number> },
    ) => {
      existingCallback?.(
        containerName,
        adsLoaded,
        isExperimentVariant,
        callbackOptions,
      );
      window.__showAlgoResults?.();
    };
  }

  return decoratedUnit;
}

/**
 * Merges result-type defaults, group-level overrides, and unit hints.
 */
function resolveOverrides(
  resultType: GoogleResultType,
  unit: GoogleUnitLevelParams,
  groupOverrides: Record<string, unknown>,
): Record<string, unknown> {
  const overrides: Record<string, unknown> = {
    ...(DEFAULT_OVERRIDES[resultType] ?? {}),
    ...groupOverrides,
  };

  for (const key of BLOCK_OPTION_KEYS) {
    const value = unit[key];

    if (isValidOverrideValue(value)) {
      overrides[key] = value;
    } else if (value === null) {
      delete overrides[key];
    }
  }

  return cleanOverrides(overrides);
}

/**
 * Extracts CSA metadata from groups.
 */
function extractGroupOverrides(
  group: PAPICore.RegionGroup,
): Record<string, unknown> {
  const candidate = group as unknown as {
    csa_page_options?: Record<string, unknown>;
    metadata?: {
      csa_page_options?: Record<string, unknown>;
    };
  };

  return (
    candidate.metadata?.csa_page_options ?? candidate.csa_page_options ?? {}
  );
}

/**
 * Removes empty values from the override set.
 */
function cleanOverrides(
  overrides: Record<string, unknown>,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(overrides)) {
    if (isValidOverrideValue(value)) {
      cleaned[key] = value;
    } else if (value === null) {
      delete cleaned[key];
    }
  }

  return cleaned;
}

/**
 * Produces a deterministic key for the block based on its option set.
 */
function createBlockKey(overrides: Record<string, unknown>): string {
  const sortedEntries = Object.entries(overrides).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return JSON.stringify(sortedEntries);
}

function sendPingback(pingbackUrl: URL | null): void {
  if (!pingbackUrl) {
    return;
  }

  sendBeacon(pingbackUrl);
}

/**
 * Narrows arbitrary `result_type` values to the subset supported by CSA.
 */
function isGoogleResultType(value: unknown): value is GoogleResultType {
  return value === "ads" || value === "aylf";
}

/**
 * Type guard for valid override values.
 */
function isValidOverrideValue(
  value: unknown,
): value is string | number | boolean {
  return (
    value !== undefined &&
    value !== null &&
    (typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean")
  );
}
